import { Playlist } from "./index";

export type PosterFitMode = "auto" | "contain" | "cover" | "fill" | "blur";

export interface OfflineRoutingDiagnostics {
  source: "offline-cache";
  reason: string;
  evaluatedAtUtc: string;
  matchedRuleId?: string;
  matchedRuleName?: string;
  matchedConditionType?: string;
  matchedActionType?: string;
  targetScreenCode?: string;
}

// Live audio for household tablets, sent with the home screens only.
export interface HomeAudio {
  liveStatusUrl: string;
  liveStreams: { slug: string; name: string; hlsUrl: string }[];
  partnerAudio?: { provider: string; url: string } | null;
}

// A masjid screen the masjid ticked for homes, which households can choose (home screens only).
export interface HomeScreenChoice {
  id: number;
  name: string;
  orientation: "landscape" | "portrait";
}

export interface ScreenData {
  // Base properties
  id: number;
  code: string;
  name: string;
  masjidId: number;
  masjidName?: string;
  masjidColor?: string;
  homeAudio?: HomeAudio | null;
  homeScreens?: HomeScreenChoice[] | null;
  backgroundTemplate: string | null;
  backgroundType: string;
  backgroundUrl: string | null;
  isActive: boolean;
  isResponsive: boolean;
  orientation: "portrait" | "landscape";
  layoutData: string | null;
  layoutType: string;
  splitTemplate: string | null;
  width: number;
  height: number;
  dateCreated: string;
  dateModified: string;

  // Prayer Length Settings (in seconds)
  fajrLength: number;
  dhuhrLength: number;
  asarLength: number;
  maghribLength: number;
  ishaLength: number;
  jummahLength: number;

  // Adhan Offset Settings (in minutes before Jamaah)
  fajrAdhanOffset: number;
  dhuhrAdhanOffset: number;
  asarAdhanOffset: number;
  maghribAdhanOffset: number;
  ishaAdhanOffset: number;
  jummahAdhanOffset: number;
  adhanDurationSeconds: number;
  jummahAdhanDurationSeconds?: number;

  // Reminder Settings
  salahCountdownReminder: number;
  showFullscreenCountdown?: boolean;

  // Makrooh Time Settings (in minutes)
  sunriseMakroohPeriod: number;
  zawwalMakroohPeriod: number;
  sunsetMakroohPeriod: number;

  // Screen Blanking Settings - Fajr
  blankScreenDuringFajr: boolean;
  fajrBlankStartOffset: number;
  fajrBlankDuration: number;

  // Screen Blanking Settings - Dhuhr
  blankScreenDuringDhuhr: boolean;
  dhuhrBlankStartOffset: number;
  dhuhrBlankDuration: number;

  // Screen Blanking Settings - Asar
  blankScreenDuringAsar: boolean;
  asarBlankStartOffset: number;
  asarBlankDuration: number;

  // Screen Blanking Settings - Maghrib
  blankScreenDuringMaghrib: boolean;
  maghribBlankStartOffset: number;
  maghribBlankDuration: number;

  // Screen Blanking Settings - Isha
  blankScreenDuringIsha: boolean;
  ishaBlankStartOffset: number;
  ishaBlankDuration: number;

  // Screen Blanking Settings - Jummah
  blankScreenDuringJummah: boolean;
  jummahBlankStartOffset: number;
  jummahBlankDuration: number;

  // Screen Blanking Settings - Background
  blankScreenBackgroundUrl?: string; // Optional background image URL for blank screen during prayers

  // Screen Blanking Settings - Message Appearance
  blankScreenMessageColor?: string; // Color of the salah in progress message text
  blankScreenMessageFontScalePercent?: number; // Size scale of the message text (60-600)
  blankScreenMessageX?: number; // Horizontal position of the message (0-100%)
  blankScreenMessageY?: number; // Vertical position of the message (0-100%)

  // Screen Blanking Settings - Countdown
  blankScreenShowCountdown?: boolean; // Show countdown of remaining salah time on blank screen
  blankScreenCountdownX?: number; // Horizontal position of the countdown (0-100%)
  blankScreenCountdownY?: number; // Vertical position of the countdown (0-100%)
  blankScreenCountdownFormat?: "digital" | "short" | "medium" | "long";
  blankScreenCountdownDirection?: "down" | "up";
  blankScreenCountdownColor?: string; // Text color of the countdown
  blankScreenCountdownFontScalePercent?: number; // Size scale of the countdown (60-600)
  blankScreenCountdownFontWeight?: number; // Font weight of the countdown (400-900)

  // Display Settings
  changeIslamicDateAtMaghrib: boolean;
  /** @deprecated Always false. Events now managed through regular playlists. Retained for backward compatibility. */
  showEvents: boolean;
  eventPosterDuration: number;
  showLandscapePosterOnPortrait?: boolean; // Whether to show landscape event posters on portrait screens
  showPortraitPosterOnLandscape?: boolean; // Whether to show portrait event posters on landscape screens
  slideshowBackgroundUrl?: string; // Optional background image URL for slideshow page
  salahChangesBackgroundUrl?: string; // Optional background image URL for upcoming salah changes page
  showNextSalahChanges: boolean;
  nextSalahChangesDuration: number;
  upcomingSalahLookaheadDays?: number; // Number of days to look ahead for upcoming salah changes (1-7)
  hideUpcomingSalahWhenNoChanges?: boolean; // Skip upcoming salah panel when no changes are detected
  showSalahChangesAfterPrayer?: boolean; // Show salah change overlay after each prayer if that prayer's time is changing tomorrow
  salahChangesAfterPrayerDuration?: number; // Duration in seconds to show the after-prayer salah change overlay (default 30)
  salahChangesAfterPrayerPosition?: string; // "before" or "after" — position relative to after-prayer playlists
  salahChangesAfterPrayerTemplateJson?: string; // Optional JSON layout for a custom after-prayer salah changes overlay
  afterPrayerSalahChangesBackgroundUrl?: string; // Optional background image URL for after-prayer/flash salah changes
  flashSalahChangesAfterPrayer?: boolean; // Flash the prayer's jamaat time on the timetable after prayer when it's changing tomorrow
  flashSalahChangesDuration?: number; // How long the jamaat time flashes (seconds, default 30)
  flashSalahChangesSpeed?: number; // Flash cycle speed in seconds (1=fast, 5=slow, default 1)
  flashSalahChangesColor?: string; // Optional font color for flashing prayer times (e.g. "#FF0000")
  showUpcomingEvents?: boolean; // Whether to show the upcoming events calendar widget
  showLabbaik?: boolean; // Labbaik announcements, events and appeals with QR codes
  showLabbaikEvents?: boolean; // Player-only: a layout region with "Show Playlists" off still lists Labbaik events
  upcomingEventsDuration?: number; // Duration to display the upcoming events widget (seconds)
  hideUpcomingEventsWhenNoEvents?: boolean; // Skip upcoming events panel when no events are available
  showHomePageBetweenCycles?: boolean; // For embedded playlist displays: show main page between cycles
  homePageDisplayDurationSeconds?: number; // Duration to show main page between cycles
  timesRefreshInterval: number;

  // Poster Category Filters (sub-options under showEvents)
  showEventPosters?: boolean; // Display actual event posters (lectures, conferences, etc.)
  showFuneralNotices?: boolean; // Display Janazah/funeral announcements
  showInformationPosters?: boolean; // Display general information posters
  showAnnouncements?: boolean; // Display time-sensitive announcements
  showClassEnrollments?: boolean; // Display class enrollment posters

  // Ticker Settings (styling is handled by Designer's layoutData)
  tickerEnabled?: boolean; // Enable scrolling ticker (default: true)
  tickerCategories?: string; // JSON array of categories: Event, FuneralNotice, InformationPoster

  // New display settings
  use12HourFormat?: boolean; // Whether to display times in 12-hour format
  showTimeSeconds?: boolean; // Whether to display seconds in the live clock
  timeTextColor?: string; // Live clock text color (hex)
  timeFontScalePercent?: number; // Live clock size scale percentage (70-1200)
  timeFontWeight?: number; // Live clock font weight (400-900)
  timeFontFamily?: string; // Live clock font family token
  salahChangesOrder?: string; // "DateOrder" or "SalahOrder"
  gregorianDateFormat?: string; // Gregorian date format string
  gregorianDateTextColor?: string; // Gregorian date text color (hex)
  gregorianDateFontScalePercent?: number; // Gregorian date size scale percentage (70-1200)
  gregorianDateFontWeight?: number; // Gregorian date font weight (400-900)
  gregorianDateFontFamily?: string; // Gregorian date font family token
  gregorianDateAlignment?: "left" | "center" | "right"; // Gregorian date horizontal alignment
  hijriDateFormat?: string; // Hijri date format string
  hijriDateTextColor?: string; // Hijri date text color (hex)
  hijriDateFontScalePercent?: number; // Hijri date size scale percentage (70-1200)
  hijriDateFontWeight?: number; // Hijri date font weight (400-900)
  hijriDateFontFamily?: string; // Hijri date font family token
  hijriDateAlignment?: "left" | "center" | "right"; // Hijri date horizontal alignment
  countdownTextColor?: string; // Countdown timer text color
  countdownBackgroundUrl?: string; // Optional background image for fullscreen countdown takeover
  countdownTitleColor?: string; // Fullscreen countdown title text color
  countdownBackgroundColor?: string; // Fullscreen countdown background color fallback
  countdownTimeFormatStyle?: "short" | "medium" | "long" | "digital"; // Fullscreen countdown value format style
  countdownTitleFontScalePercent?: number; // Fullscreen countdown title size scale percentage (60-600)
  countdownTitleFontWeight?: number; // Fullscreen countdown title font weight (400-900)
  countdownValueFontScalePercent?: number; // Fullscreen countdown value size scale percentage (60-600)
  countdownValueFontWeight?: number; // Fullscreen countdown value font weight (400-900)
  currentSalahHighlightColor?: string; // Color used when a salah is currently in progress

  // Screen-level Color Theme
  primaryColor?: string; // Primary color for the screen theme (e.g., #FFC107)
  secondaryColor?: string; // Secondary color for the screen theme (e.g., #FF9800)
  accentColor?: string; // Accent color for highlights and special elements (e.g., #4CAF50)
  widgetBackgroundColor?: string; // Background color for widgets like Salah Changes and Events (e.g., #000000)
  headingTextColor?: string; // Text color for widget headings like Upcoming Salah Changes and Events (e.g., #FFFFFF)

  // Night comfort mode settings
  enableNightMode?: boolean;
  nightModeTriggerType?: "auto" | "prayer";
  nightModeStartPrayer?: string;
  nightModeEndPrayer?: string;
  nightModeIntensity?: number;
  nightModeSecondsAfterStartPrayerEnds?: number | null;
  nightModeSecondsBeforeEndPrayer?: number | null;

  // Routing metadata returned by backend
  routingEnabled?: boolean;
  routingMatched?: boolean;
  routingMatchReason?: string;
  matchedRuleId?: string | null;
  matchedRuleName?: string | null;
  matchedConditionType?: string | null;
  matchedActionType?: string | null;
  appliedIntensityPercent?: number | null;
  resolvedFromScreenId?: number;
  resolvedToScreenId?: number;
  resolvedFromScreenCode?: string;
  resolvedToScreenCode?: string;

  // Rotation settings
  prayerPageDuration?: number; // Make optional with ?

  // Salah Blanking Behavior
  continuePlaylistsAfterBlank?: boolean; // Whether to continue playlists where they left off after screen blank
  skipPlaylistsDuringSalah?: boolean; // Whether to skip scheduled playlists during prayer times

  // Makrooh Settings
  showMakroohWarnings?: boolean; // Whether to show makrooh warnings
  pausePlaylistsDuringMakrooh?: boolean; // Whether to pause playlists during makrooh times
  makroohWarningDuration?: number; // How long to show makrooh warning (in seconds)
  showMakroohTimer?: boolean; // Whether to show countdown timer on Makrooh warning
  makroohTimerLabel?: string; // Case-sensitive countdown label text for Makrooh warning

  // Advanced Islamic Calendar Settings
  enableIslamicCalendarEvents?: boolean; // Whether to enable special Islamic date based content

  // Add customized names
  customizedNames: {
    // Prayer time names
    date: string;
    day: string;
    hijriDate: string;
    sehri: string;
    fajr: string;
    sunrise: string;
    noon: string;
    dhuhr: string;
    asar: string;
    sunset: string;
    maghrib: string;
    isha: string;
    jummah: string;
    // A household tablet's word for the congregation prayer (Jama'ah, Jamaat, Iqamah…).
    jamaat?: string;

    // Islamic month names
    muharram: string;
    safar: string;
    rabeeUlAwwal: string;
    rabeeUlAkhir: string;
    jamadahAlAwwal: string;
    jamadahAlAkhir: string;
    rajab: string;
    shaban: string;
    ramadhan: string;
    shawwal: string;
    dhulQadah: string;
    dhulHijjah: string;

    // Short month names (MMM); absent in config cached before they existed
    muharramShort?: string;
    safarShort?: string;
    rabeeUlAwwalShort?: string;
    rabeeUlAkhirShort?: string;
    jamadahAlAwwalShort?: string;
    jamadahAlAkhirShort?: string;
    rajabShort?: string;
    shabanShort?: string;
    ramadhanShort?: string;
    shawwalShort?: string;
    dhulQadahShort?: string;
    dhulHijjahShort?: string;
  };

  // Azure Best Practice: Add new properties with defaults for backward compatibility
  backgroundOverlayType?: "dark" | "light" | "blur" | "gradient" | "none";
  backgroundOverlayOpacity?: number;

  // Azure Best Practice: Add new property with optional chaining for backward compatibility
  playlists?: Playlist[];
  timeZone?: string; // Time zone for the masjid, e.g., "Europe/London"

  // ✅ Updated noticeboard settings - column-based configuration
  displayMode?: "slideshow" | "noticeboard"; // Default: "slideshow"
  noticeboardSettings?: {
    slideshowPosterFitMode?: PosterFitMode;
    noticeboardPosterFitMode?: PosterFitMode;
    columns: number; // ✅ NEW: User-specified columns (2-6)
    autoFitRows: boolean; // ✅ NEW: Auto-calculate rows based on items
    maxRowsPerPage?: number | null; // ✅ NEW: Optional limit rows per page
    padding: number; // Padding between items (e.g., 20)
    pageTransitionDuration: number; // How long to show each page (e.g., 30 seconds)
    transitionEffect: "fade" | "slide" | "none"; // Animation between pages
    backgroundColor: string; // Background color (e.g., "#1a1a1a")
    showBorders: boolean; // Show borders around posters
    borderColor: string; // Border color
    borderWidth: number; // Border width in pixels
  };
  salahInProgressMessage: string; // Message to show when a prayer is in progress

  // Hybrid routing snapshot metadata for offline-safe evaluation
  routingRulesJson?: string;
  routingSchemaVersion?: number;
  routingSnapshotFetchedAtUtc?: string;
  routingRulesTtlSeconds?: number;
  offlineRoutingDiagnostics?: OfflineRoutingDiagnostics;
}

export interface ScreenPosition {
  x: number;
  y: number;
}

export interface ScreenSize {
  width: number;
  height: number;
}

/**
 * Type guard to check if a screen data object contains all essential properties
 * required for core functionality
 * @param screen The screen data object to check
 */
export function hasEssentialScreenSettings(
  screen: Partial<ScreenData>,
): screen is ScreenData {
  return (
    typeof screen.id === "number" &&
    typeof screen.code === "string" &&
    typeof screen.masjidId === "number" &&
    (screen.orientation === "portrait" || screen.orientation === "landscape") &&
    typeof screen.isActive === "boolean"
  );
}

// Add a utility function to get the value with default
export function getPrayerPageDuration(screenData: ScreenData): number {
  return screenData.prayerPageDuration ?? 30; // Default to 30 seconds if not specified
}

// Add utility functions for new properties with defaults
export function shouldContinuePlaylistsAfterBlank(
  screenData: ScreenData,
): boolean {
  return screenData.continuePlaylistsAfterBlank ?? true; // Default to true
}

export function shouldSkipPlaylistsDuringSalah(
  screenData: ScreenData,
): boolean {
  return screenData.skipPlaylistsDuringSalah ?? true; // Default to true
}

export function shouldShowMakroohWarnings(screenData: ScreenData): boolean {
  return screenData.showMakroohWarnings ?? true; // Default to true
}

export function shouldPausePlaylistsDuringMakrooh(
  screenData: ScreenData,
): boolean {
  return screenData.pausePlaylistsDuringMakrooh ?? true; // Default to true
}

export function getMakroohWarningDuration(screenData: ScreenData): number {
  return screenData.makroohWarningDuration ?? 60; // Default to 60 seconds
}

export function shouldEnableIslamicCalendarEvents(
  screenData: ScreenData,
): boolean {
  return screenData.enableIslamicCalendarEvents ?? true; // Default to true
}

// Add utility functions at the bottom of the file
export function shouldUse12HourFormat(screenData: ScreenData): boolean {
  return screenData.use12HourFormat ?? true; // Default to true if not specified
}

export function getSalahChangesOrder(screenData: ScreenData): string {
  return screenData.salahChangesOrder ?? "DateOrder"; // Default to DateOrder if not specified
}

export function getUpcomingSalahLookaheadDays(screenData: ScreenData): number {
  const value = screenData.upcomingSalahLookaheadDays ?? 7;
  return Math.min(Math.max(value, 1), 7);
}

/**
 * Get noticeboard settings with defaults
 */
export function getNoticeboardSettings(screenData: ScreenData) {
  return (
    screenData.noticeboardSettings || {
      slideshowPosterFitMode: "auto" as const,
      noticeboardPosterFitMode: "auto" as const,
      columns: 4,
      autoFitRows: true,
      maxRowsPerPage: null,
      padding: 20,
      pageTransitionDuration: 30,
      transitionEffect: "fade" as const,
      backgroundColor: "#1a1a1a",
      showBorders: true,
      borderColor: "#333333",
      borderWidth: 2,
    }
  );
}

/**
 * Poster Category enum values (matches backend PosterCategory)
 */
export enum PosterCategory {
  Event = "Event",
  FuneralNotice = "FuneralNotice",
  InformationPoster = "InformationPoster",
}

/**
 * Check if a specific poster category should be shown based on screen settings
 */
export function shouldShowCategory(
  screenData: ScreenData,
  category: string,
): boolean {
  switch (category) {
    case PosterCategory.Event:
      return screenData.showEventPosters ?? true;
    case PosterCategory.FuneralNotice:
      return screenData.showFuneralNotices ?? true;
    case PosterCategory.InformationPoster:
      return screenData.showInformationPosters ?? true;
      return true; // Always show miscellaneous
    default:
      return true; // Unknown categories are shown by default
  }
}

/**
 * Get ticker settings with defaults
 */
/**
 * Get ticker settings from screen data
 * Note: Styling (speed, fontSize, colors) is now handled by Designer's layoutData
 */
export function getTickerSettings(screenData: ScreenData) {
  return {
    enabled: screenData.tickerEnabled ?? true, // Default to true
    categories: screenData.tickerCategories
      ? JSON.parse(screenData.tickerCategories)
      : [PosterCategory.Event],
  };
}

/**
 * Check if an event should be shown in the ticker based on its category
 */
export function shouldShowInTicker(
  screenData: ScreenData,
  eventCategory: string,
): boolean {
  const tickerSettings = getTickerSettings(screenData);
  if (!tickerSettings.enabled) return false;
  return tickerSettings.categories.includes(eventCategory);
}
